import { useCallback } from 'react';

interface SimpleMarkdownEditorProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  minRows?: number;
}

export function SimpleMarkdownEditor({ value, onChange, placeholder = 'Type here... You can use basic markdown like **bold**, *italic*, and [links](https://...)', minRows = 10 }: SimpleMarkdownEditorProps) {
  const applyWrap = useCallback((prefix: string, suffix: string) => {
    const textarea = document.getElementById('sme-textarea') as HTMLTextAreaElement | null;
    if (!textarea) return;
    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const before = value.slice(0, start);
    const selected = value.slice(start, end);
    const after = value.slice(end);
    const next = `${before}${prefix}${selected || 'text'}${suffix}${after}`;
    onChange(next);
    setTimeout(() => {
      const cursorStart = start + prefix.length;
      const cursorEnd = cursorStart + (selected ? selected.length : 4);
      textarea.focus();
      textarea.setSelectionRange(cursorStart, cursorEnd);
    }, 0);
  }, [onChange, value]);

  const applyInsert = useCallback((snippet: string) => {
    const textarea = document.getElementById('sme-textarea') as HTMLTextAreaElement | null;
    if (!textarea) return;
    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const next = `${before}${snippet}${after}`;
    onChange(next);
    setTimeout(() => {
      const cursor = start + snippet.length;
      textarea.focus();
      textarea.setSelectionRange(cursor, cursor);
    }, 0);
  }, [onChange, value]);

  return (
    <div className="border rounded-md">
      <div className="flex flex-wrap items-center gap-2 p-2 border-b bg-gray-50">
        <button type="button" className="px-2 py-1 text-xs rounded border bg-white hover:bg-gray-100" onClick={() => applyWrap('**', '**')}>Bold</button>
        <button type="button" className="px-2 py-1 text-xs rounded border bg-white hover:bg-gray-100" onClick={() => applyWrap('*', '*')}>Italic</button>
        <button type="button" className="px-2 py-1 text-xs rounded border bg-white hover:bg-gray-100" onClick={() => applyWrap('`', '`')}>Code</button>
        <button type="button" className="px-2 py-1 text-xs rounded border bg-white hover:bg-gray-100" onClick={() => applyInsert('\n\n- Item 1\n- Item 2\n- Item 3\n')}>List</button>
        <button type="button" className="px-2 py-1 text-xs rounded border bg-white hover:bg-gray-100" onClick={() => applyInsert('\n\n1. First\n2. Second\n3. Third\n')}>Numbered</button>
        <button type="button" className="px-2 py-1 text-xs rounded border bg-white hover:bg-gray-100" onClick={() => applyInsert('\n\n[link text](https://example.com)\n')}>Link</button>
        <button type="button" className="px-2 py-1 text-xs rounded border bg-white hover:bg-gray-100" onClick={() => applyInsert('\n\n> Quote\n')}>Quote</button>
      </div>
      <textarea
        id="sme-textarea"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full p-3 font-sans text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder={placeholder}
        rows={minRows}
      />
      <div className="p-2 text-xs text-gray-500 border-t bg-gray-50">
        Supports basic markdown: **bold**, *italic*, `code`, lists, quotes, and links.
      </div>
    </div>
  );
}


